import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import axios from 'axios';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { Button } from 'ui-components/components/button';
import { Input } from 'ui-components/components/input';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import { Gap } from 'ui-components/components/flex/gap';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import ModalPortal from '../../../../../components/modal-portal';
import { getProvidersRequest } from '../../../../../store/freight-settings/freight-settings.actions';
import { connectModalMessages } from '../../messages';
import style from './connect-freight-provider-modal.module.less';

type ConnectFreightProviderModalProps = {
  freightProviderId: number;
  providerName: string;
  onClose: () => void;
};

export const ConnectFreightProviderModal = ({ freightProviderId, providerName, onClose }: ConnectFreightProviderModalProps) => {
  const dispatch = useDispatch();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isConnectDisabled = !clientId || !clientSecret || isConnecting;

  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    setErrorMessage('');

    const payload = {
      freightProviderId,
      clientId,
      clientSecret,
      // TODO: clean this up before any user usage
      audience: 'staging-wwex-apig',
      apiBaseUrl: 'https://speedship.staging-wwex.com',
      authUrl: 'https://auth.staging-wwex.com',
    };

    try {
      await axios.post('/freight/connect', payload);
      dispatch(getProvidersRequest());
      NotificationService.showNotification({
        title: '',
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        content: <FormattedMessage {...connectModalMessages.connectSuccess} values={{ providerName }} />,
        duration: 3000,
      });
      onClose();
    } catch {
      setErrorMessage('error');
      NotificationService.showNotification({
        title: '',
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: <FormattedMessage {...connectModalMessages.connectError} />,
        duration: 3000,
      });
    } finally {
      setIsConnecting(false);
    }
  }, [clientId, clientSecret, dispatch, onClose, freightProviderId, providerName]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={style['connect-freight-provider-modal']}>
        <ModalHeader closeModal={onClose}>
          <FormattedMessage {...connectModalMessages.title} values={{ providerName }} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          <FlexColumn gap={Gap.Space16}>
            <Text className={style.description} textType={TextType.Description} value={connectModalMessages.description} />

            <FlexColumn gap={Gap.Space4}>
              <Text textType={TextType.Label} value={connectModalMessages.clientIdLabel} />
              <Input hasError={!!errorMessage} onChange={setClientId} type='text' value={clientId} />
            </FlexColumn>

            <FlexColumn gap={Gap.Space4}>
              <Text textType={TextType.Label} value={connectModalMessages.clientSecretLabel} />
              <Input hasError={!!errorMessage} onChange={setClientSecret} type='password' value={clientSecret} />
            </FlexColumn>
          </FlexColumn>
        </ModalBody>
        <ModalFooter className={style['modal-footer']}>
          <FlexRow className={style['footer-buttons']} gap={Gap.Space8}>
            <Button onClick={onClose} theme='tertiary'>
              <Text textType={TextType.Caption} value={connectModalMessages.cancelButton} />
            </Button>
            <Button disabled={isConnectDisabled} onClick={handleConnect} theme='primary'>
              <Text textType={TextType.Caption} value={connectModalMessages.connectButton} />
            </Button>
          </FlexRow>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
